import { config } from "../../../config";
import { AVAILABLE_MODELS, AvailableModel, PRODUCTION_MODEL, ThinkingModeEffortLevel } from "../../../services/claude";
import { ArtifactType } from "../../../types/artifact";
import { logError } from "../../../utils/error";
import { ENLIGHTENME_DEFAULT_SYSTEM_PROMPT } from "../constants/enlightenmeSystemPrompt";
import { EMPTY_LENGTH } from "../constants/numeric";
import { AnswerMode, ChatMessage } from "../stores/chatTypes";

// Storage keys
const TEMPERATURE_KEY = "enlighten_ragchat_temperature";
const PREVIOUS_SESSION_KEY = "enlighten_ragchat_previous_session";
const ARTIFACT_TYPES_KEY = "enlighten_ragchat_artifact_types";
const ENLIGHTENME_SYSTEM_PROMPT_KEY = "enlighten_ragchat_enlightenme_system_prompt";
const THINKING_MODE_KEY = "enlighten_ragchat_thinking_mode";
const MAX_TOKENS_KEY = "enlighten_ragchat_max_tokens";
const BUDGET_TOKENS_KEY = "enlighten_ragchat_budget_tokens";
const MAX_INPUT_TOKENS_KEY = "enlighten_ragchat_max_input_tokens";
const MODEL_KEY = "enlighten_ragchat_model";
const EFFORT_KEY = "enlighten_ragchat_effort";
const ANSWER_MODE_KEY = "enlighten_ragchat_answer_mode";

// Default values
const DEFAULT_TEMPERATURE = 0.1;
const DEFAULT_ARTIFACT_TYPES = [ArtifactType.Summary];
const DEFAULT_THINKING_MODE = true;
const DEFAULT_MAX_TOKENS = 64000;
const DEFAULT_BUDGET_TOKENS = 2000;
const DEFAULT_MAX_INPUT_TOKENS = 500000;
const MIN_INPUT_TOKENS = 0;
const DEFAULT_MODEL: AvailableModel = config.defaultClaudeModel as AvailableModel;
const DEFAULT_EFFORT: ThinkingModeEffortLevel = "high";
const DEFAULT_ANSWER_MODE: AnswerMode = "quick";

export const RAGCHAT_DEFAULTS = {
  temperature: DEFAULT_TEMPERATURE,
  thinkingMode: DEFAULT_THINKING_MODE,
  maxTokens: DEFAULT_MAX_TOKENS,
  budgetTokens: DEFAULT_BUDGET_TOKENS,
  maxInputTokens: DEFAULT_MAX_INPUT_TOKENS,
  model: DEFAULT_MODEL,
  effort: DEFAULT_EFFORT,
  answerMode: DEFAULT_ANSWER_MODE,
  includedArtifactTypes: DEFAULT_ARTIFACT_TYPES
} as const;

/**
 * Saves the current temperature setting to localStorage
 * @param temperature - The temperature value to save
 */
export const saveTemperature = (temperature: number): void => {
  try {
    localStorage.setItem(TEMPERATURE_KEY, temperature.toString());
  } catch (error) {
    logError("Failed to save temperature to localStorage", error);
  }
};

/**
 * Retrieves the saved temperature setting from localStorage
 * @returns The saved temperature or default value if not found
 */
export const getSavedTemperature = (): number => {
  try {
    const savedValue = localStorage.getItem(TEMPERATURE_KEY);
    if (savedValue === null) {
      return DEFAULT_TEMPERATURE;
    }

    const parsedValue = parseFloat(savedValue);
    return isNaN(parsedValue) ? DEFAULT_TEMPERATURE : parsedValue;
  } catch (error) {
    logError("Failed to retrieve temperature from localStorage", error);
    return DEFAULT_TEMPERATURE;
  }
};

/**
 * Saves the current artifact types selection to localStorage
 * @param artifactTypes - Array of selected artifact types
 */
export const saveArtifactTypes = (artifactTypes: ArtifactType[]): void => {
  try {
    localStorage.setItem(ARTIFACT_TYPES_KEY, JSON.stringify(artifactTypes));
  } catch (error) {
    logError("Failed to save artifact types to localStorage", error);
  }
};

/**
 * Retrieves the saved artifact types from localStorage
 * @returns Array of saved artifact types or default values if not found
 */
export const getSavedArtifactTypes = (): ArtifactType[] => {
  try {
    const savedValue = localStorage.getItem(ARTIFACT_TYPES_KEY);
    if (savedValue === null) {
      return DEFAULT_ARTIFACT_TYPES;
    }

    const parsedValue = JSON.parse(savedValue) as ArtifactType[];

    if (parsedValue.length === EMPTY_LENGTH) {
      return DEFAULT_ARTIFACT_TYPES;
    }

    return parsedValue;
  } catch (error) {
    logError("Failed to retrieve artifact types from localStorage", error);
    return DEFAULT_ARTIFACT_TYPES;
  }
};

export const saveEnlightenMeSystemPrompt = (prompt: string): void => {
  try {
    localStorage.setItem(ENLIGHTENME_SYSTEM_PROMPT_KEY, prompt);
  } catch (error) {
    logError("Failed to save EnlightenMe system prompt to localStorage", error);
  }
};

export const getEnlightenMeSystemPrompt = (): string => {
  try {
    const savedValue = localStorage.getItem(ENLIGHTENME_SYSTEM_PROMPT_KEY);
    return savedValue !== null && savedValue.trim() !== ""
      ? savedValue
      : ENLIGHTENME_DEFAULT_SYSTEM_PROMPT;
  } catch (error) {
    logError("Failed to retrieve EnlightenMe system prompt from localStorage", error);
    return ENLIGHTENME_DEFAULT_SYSTEM_PROMPT;
  }
};

/**
 * Saves the thinking mode setting to localStorage
 * @param enabled - Whether thinking mode is enabled
 */
export const saveThinkingMode = (enabled: boolean): void => {
  try {
    localStorage.setItem(THINKING_MODE_KEY, enabled.toString());
  } catch (error) {
    logError("Failed to save thinking mode to localStorage", error);
  }
};

/**
 * Retrieves the saved thinking mode setting from localStorage
 * @param thinkingModeEnabled - Whether the thinking mode feature flag is enabled
 * @returns The saved thinking mode state or default value if not found
 */
export const getSavedThinkingMode = (thinkingModeEnabled: boolean): boolean => {
  if (!thinkingModeEnabled) {
    return false;
  }
  try {
    const savedValue = localStorage.getItem(THINKING_MODE_KEY);
    if (savedValue === null) {
      return DEFAULT_THINKING_MODE;
    }

    return savedValue === "true";
  } catch (error) {
    logError("Failed to retrieve thinking mode from localStorage", error);
    return DEFAULT_THINKING_MODE;
  }
};

export const saveMaxTokens = (tokens: number): void => {
  try {
    localStorage.setItem(MAX_TOKENS_KEY, tokens.toString());
  } catch (error) {
    logError("Failed to save max tokens to localStorage", error);
  }
};

export const getSavedMaxTokens = (): number => {
  try {
    const savedValue = localStorage.getItem(MAX_TOKENS_KEY);
    if (savedValue === null) {
      return DEFAULT_MAX_TOKENS;
    }

    const parsedValue = parseInt(savedValue, 10);
    return isNaN(parsedValue) ? DEFAULT_MAX_TOKENS : parsedValue;
  } catch (error) {
    logError("Failed to retrieve max tokens from localStorage", error);
    return DEFAULT_MAX_TOKENS;
  }
};

export const saveBudgetTokens = (tokens: number): void => {
  try {
    localStorage.setItem(BUDGET_TOKENS_KEY, tokens.toString());
  } catch (error) {
    logError("Failed to save budget tokens to localStorage", error);
  }
};

export const getSavedBudgetTokens = (): number => {
  try {
    const savedValue = localStorage.getItem(BUDGET_TOKENS_KEY);
    if (savedValue === null) {
      return DEFAULT_BUDGET_TOKENS;
    }

    const parsedValue = parseInt(savedValue, 10);
    return isNaN(parsedValue) ? DEFAULT_BUDGET_TOKENS : parsedValue;
  } catch (error) {
    logError("Failed to retrieve budget tokens from localStorage", error);
    return DEFAULT_BUDGET_TOKENS;
  }
};

export const saveMaxInputTokens = (tokens: number): void => {
  try {
    const clampedTokens = Math.min(Math.max(tokens, MIN_INPUT_TOKENS), DEFAULT_MAX_INPUT_TOKENS);
    localStorage.setItem(MAX_INPUT_TOKENS_KEY, clampedTokens.toString());
  } catch (error) {
    logError("Failed to save max input tokens to localStorage", error);
  }
};

export const getSavedMaxInputTokens = (): number => {
  try {
    const savedValue = localStorage.getItem(MAX_INPUT_TOKENS_KEY);
    if (savedValue === null) {
      return DEFAULT_MAX_INPUT_TOKENS;
    }

    const parsedValue = parseInt(savedValue, 10);
    if (isNaN(parsedValue)) {
      return DEFAULT_MAX_INPUT_TOKENS;
    }
    // Clamp legacy values (e.g. 1M, set before we lowered Sonnet 4.6's
    // user-facing cap to 500K) so the UI doesn't load over-limit defaults.
    return Math.min(parsedValue, DEFAULT_MAX_INPUT_TOKENS);
  } catch (error) {
    logError("Failed to retrieve max input tokens from localStorage", error);
    return DEFAULT_MAX_INPUT_TOKENS;
  }
};

export const saveModel = (model: AvailableModel): void => {
  try {
    localStorage.setItem(MODEL_KEY, model);
  } catch (error) {
    logError("Failed to save model to localStorage", error);
  }
};

export const getSavedModel = (): AvailableModel => {
  try {
    const savedValue = localStorage.getItem(MODEL_KEY) as AvailableModel;
    if (savedValue === null) {
      return DEFAULT_MODEL;
    }

    const validModelIds = AVAILABLE_MODELS.map(m => m.id);
    if (!validModelIds.includes(savedValue)) {
      return PRODUCTION_MODEL.id;
    }

    return savedValue;
  } catch (error) {
    logError("Failed to retrieve model from localStorage", error);
    return DEFAULT_MODEL;
  }
};

export const saveEffort = (effort: ThinkingModeEffortLevel): void => {
  try {
    localStorage.setItem(EFFORT_KEY, effort);
  } catch (error) {
    logError("Failed to save effort to localStorage", error);
  }
};

const VALID_EFFORTS: ThinkingModeEffortLevel[] = ["high", "medium", "low"];
const VALID_ANSWER_MODES: AnswerMode[] = ["quick", "deep", "none"];

const getSavedEnumValue = <T extends string>(key: string, validValues: T[], defaultValue: T): T => {
  try {
    const savedValue = localStorage.getItem(key);
    if (savedValue !== null && validValues.includes(savedValue as T)) {
      return savedValue as T;
    }
    return defaultValue;
  } catch (error) {
    logError(`Failed to retrieve ${key} from localStorage`, error);
    return defaultValue;
  }
};

export const getSavedEffort = (): ThinkingModeEffortLevel =>
  getSavedEnumValue(EFFORT_KEY, VALID_EFFORTS, DEFAULT_EFFORT);

const stripContextData = ({ contextData: _contextData, ...rest }: ChatMessage): Omit<ChatMessage, "contextData"> => rest;

export const savePreviousSession = (messages: ChatMessage[]): boolean => {
  try {
    const sanitized = messages.map(stripContextData);
    localStorage.setItem(PREVIOUS_SESSION_KEY, JSON.stringify(sanitized));
    return true;
  } catch (error) {
    logError("Failed to save previous session to localStorage", error);
    return false;
  }
};

export const getSavedPreviousSession = (): ChatMessage[] | null => {
  try {
    const savedValue = localStorage.getItem(PREVIOUS_SESSION_KEY);
    if (savedValue === null) {
      return null;
    }

    const parsed = JSON.parse(savedValue);
    if (!Array.isArray(parsed)) {
      return null;
    }
    return (parsed as ChatMessage[]).map((msg) => ({
      ...msg,
      timestamp: new Date(msg.timestamp)
    }));
  } catch (error) {
    logError("Failed to retrieve previous session from localStorage", error);
    return null;
  }
};

export const clearPreviousSession = (): void => {
  try {
    localStorage.removeItem(PREVIOUS_SESSION_KEY);
  } catch (error) {
    logError("Failed to clear previous session from localStorage", error);
  }
};

export const hasPreviousSession = (): boolean => {
  try {
    return localStorage.getItem(PREVIOUS_SESSION_KEY) !== null;
  } catch (error) {
    logError("Failed to check previous session in localStorage", error);
    return false;
  }
};

export const saveAnswerMode = (mode: AnswerMode): void => {
  try {
    localStorage.setItem(ANSWER_MODE_KEY, mode);
  } catch (error) {
    logError("Failed to save answer mode to localStorage", error);
  }
};

export const getSavedAnswerMode = (): AnswerMode =>
  getSavedEnumValue(ANSWER_MODE_KEY, VALID_ANSWER_MODES, DEFAULT_ANSWER_MODE);
